import React, { useEffect, useContext, useState, useRef } from "react";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  formatPrice,
  getSystemSettings,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import { ToastError } from "../../../../functions/ToastShow";

//3rd party packages
import { Helmet } from "react-helmet";
import Moment from "react-moment";
import moment from 'moment';
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useReactToPrint } from "react-to-print";

//pages & includes
import ReportSidebar from "./ReportSidebar";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";

//context consumer
import { SettingsContext } from "../../../../contexts/Settings";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import CustomSpinLoader from "../../../components/CustomSpinLoader";

const CombinedDiscountReport = () => {
  const { t } = useTranslation();
  //getting context values here
  let { loading, setLoading, dataPaginating, setDataPaginating, generalSettings } = useContext(SettingsContext);
  let { branchForSearch } = useContext(RestaurantContext);

  //all data
  const [reportData, setReportData] = useState(null);
  const componentRef = useRef();
  const componentRefThermal = useRef();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [branch, setBranch] = useState(null);
  const [siteName, setSiteName] = useState("");
  const [option, setOption] = useState("excel");

  useEffect(() => {
    setSiteName(getSystemSettings(generalSettings, "siteName"));

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [generalSettings]);

  const thermalPrint = useReactToPrint({
    content: () => componentRefThermal.current,
    pageStyle: `@page { margin: 10px;}`,
  });

  const handleBranch = (brnch) => {
    setBranch(brnch)
  }

  const handleOption = (e) => {
    setOption(e.target.value)
  }

  const getBranchWiseSelected = () => {
    setReportData(null);

    if (startDate !== null && endDate !== null && branch !== null) {
      setDataPaginating(true);

      var fromDate = moment(startDate).format("YYYY-MM-DD");
      var toDate = moment(endDate).format("YYYY-MM-DD");

      let apiUrl = BASE_URL + "/settings/get-combined-discount-report";
      let formData = new FormData();

      formData.append("fromDate", fromDate);
      formData.append("toDate", toDate);
      formData.append("branch_id", branch?.id);

      axios.post(apiUrl, formData, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        })
        .then((res) => {
          setReportData(res.data)
          setDataPaginating(false);
          if (option === "excel") {
            componentRef.current.handleDownload();
          } else {
            thermalPrint();
          }
        })
        .catch((error) => {
          setDataPaginating(false);
        });
    } else {
      ToastError({title: `${"Please select from date, to date and branch"}`})
    }
  };

  return (
    <>
      <Helmet>
        <title>{_t(t("Combined Discount Report"))}</title>
      </Helmet>

      <CombinedDiscountReportExcelPrintView
        reportData={reportData}
        startDate={startDate}
        endDate={endDate}
        siteName={siteName}
        componentRefThermal={componentRefThermal}
        componentRef={componentRef}
        t={t}
      />

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                  <div className="t-pl-15 t-pr-15">
                    <CustomSpinLoader loading={loading || dataPaginating} />

                    <div key="smtp-form">
                      <div className="gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="row t-mb-15 mb-md-0" >
                          <ul className="t-list fk-breadcrumb col-6">
                            <li className="fk-breadcrumb__list">
                              <span className="pl-3 t-link fk-breadcrumb__link text-capitalize">
                                {_t(t("Combined Discount Report"))}
                              </span>
                            </li>
                          </ul>
                          <div className="col-6 d-flex justify-content-end p-0">
                            <ReportTypeOptions
                              selectedOption={option}
                              onChange={handleOption}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-md-2 col-sm-3 d-md-block">
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control t-mb-15 mb-md-0"
                            placeholderText={_t(t("From date"))}
                            shouldCloseOnSelect={false}
                          />
                        </div>

                        <div className="col-md-2 col-sm-3 d-md-block">
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control t-mb-15 mb-md-0"
                            placeholderText={_t(t("To date"))}
                            shouldCloseOnSelect={false}
                          />
                        </div>

                        <div className="col-md-3 col-sm-4">
                          <Select
                            options={branchForSearch ?? []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={branch}
                            classNamePrefix="select"
                            className="table-text"
                            onChange={handleBranch}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select a branch")) + ".."}
                          />
                        </div>
                        
                        <div className="col-md-3 col-sm-1">
                        </div>
                        <div className="col-md-2 col-sm-1 t-mb-15 mb-md-0 d-none d-md-block text-right">
                          <button
                            className="ml-1 btn btn-block btn-primary sm-text py-2 rounded"
                            onClick={(e) => getBranchWiseSelected()}
                          >
                            REPORT
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
    </>
  );
}

export default CombinedDiscountReport;

const CombinedDiscountReportExcelPrintView = ({ reportData, startDate, endDate, siteName, componentRefThermal, componentRef }) => {
  let grandDisTotal = {
    sub_total: 0,
    discount: 0,
    discount_amount: 0,
    vat: 0,
    service_amount: 0,
    service_charge: 0,
    total_payable: 0,
  };

  let grandCardTotal = {
    order_bill: 0,
    vat: 0,
    discount: 0,
    discount_amount: 0,
    total_payable: 0,
    claimable_amount: 0,
    unclaimable_amount: 0,
  };
  
  return (
    <>
      <ReactHTMLTableToExcel
        id="test-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls"
        filename="Combined Discount Report"
        sheet="sheet 1"
        buttonText="Combined Discount Report"
        ref={componentRef}
      />

      {/* Excel & print */}
      <div className="d-none">
        <div ref={componentRefThermal}>
          <div className="fk-print">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  {reportData !== null && reportData !== undefined && (
                    <div className="col-12 t-mb-15 mb-md-0 table-responsive">
                      <table className="table mt-3" id="table-to-xls" style={{ borderCollapse: "collapse" }}>
                        <thead className="align-middle">
                          <tr style={{ border: "0px" }}>
                            <th colSpan="15">
                              <div className="row">
                                <div className="col-12">
                                  <h3 className="text-center mb-0">{siteName}</h3>
                                  <h3 className="text-center mt-0">Combined Discount Report</h3>
                                  <p className="text-center">
                                    FROM&nbsp;
                                    <Moment format="LL">{startDate}</Moment>
                                    &nbsp;TO&nbsp;
                                    <Moment format="LL">{endDate}</Moment>
                                  </p>
                                </div>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        
                        {/* Card Report Section */}
                        {reportData?.card_report && Object.keys(reportData.card_report).length > 0 && (
                          <>
                            <thead>
                              <tr><th colSpan="14" className="text-center font-weight-bold lg-text border-0 pt-3">Card Report</th></tr>
                              <tr>
                                <th>S/L</th>
                                <th>Card Number</th>
                                <th>Token</th>
                                <th>Scheme Name</th>
                                <th style={{minWidth: "110px"}}>Order Date</th>
                                <th>Order Bill</th>
                                <th>Tax</th>
                                <th>Discount %</th>
                                <th>Discount Amount</th>
                                <th>Total Payable</th>
                                <th>Claimable Amount</th>
                                <th>Unclaimable Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.keys(reportData.card_report).map((group, groupIndex) => {
                                  let groupCardTotal = {
                                    order_bill: 0,
                                    vat: 0,
                                    discount: 0,
                                    discount_amount: 0,
                                    total_payable: 0,
                                    claimable_amount: 0,
                                    unclaimable_amount: 0,
                                  };

                                  return (
                                    <React.Fragment key={`card_group_${groupIndex}`}>
                                      <tr>
                                        <td colSpan="14" className="text-center font-weight-bold text-capitalize border-0 pt-2 pb-2 bg-light">
                                          {group}
                                        </td>
                                      </tr>
                                      {reportData.card_report[group].map((item, i) => {
                                        groupCardTotal.order_bill += parseFloat(item.order_bill || 0);
                                        groupCardTotal.vat += parseFloat(item.vat || 0);
                                        groupCardTotal.discount += parseFloat(item.discount || 0);
                                        groupCardTotal.discount_amount += parseFloat(item.discount_amount || 0);
                                        groupCardTotal.total_payable += parseFloat(item.total_payable || 0);
                                        groupCardTotal.claimable_amount += parseFloat(item.claimable_amount || 0);
                                        groupCardTotal.unclaimable_amount += parseFloat(item.unclaimable_amount || 0);

                                        grandCardTotal.order_bill += parseFloat(item.order_bill || 0);
                                        grandCardTotal.vat += parseFloat(item.vat || 0);
                                        grandCardTotal.discount += parseFloat(item.discount || 0);
                                        grandCardTotal.discount_amount += parseFloat(item.discount_amount || 0);
                                        grandCardTotal.total_payable += parseFloat(item.total_payable || 0);
                                        grandCardTotal.claimable_amount += parseFloat(item.claimable_amount || 0);
                                        grandCardTotal.unclaimable_amount += parseFloat(item.unclaimable_amount || 0);

                                        return (
                                          <tr key={`card_item_${i}`}>
                                            <td className="text-center">{i + 1}</td>
                                            <td className="text-center">{item.card_number}</td>
                                            <td className="text-center">{item.token?.id || item.token}</td>
                                            <td className="text-center">{item.scheme_name}</td>
                                            <td className="text-center">{item.order_date}</td>
                                            <td className="text-center">{item.order_bill}</td>
                                            <td className="text-center">{item.vat}</td>
                                            <td className="text-center">{item.discount}%</td>
                                            <td className="text-center">{item.discount_amount}</td>
                                            <td className="text-center">{item.total_payable}</td>
                                            <td className="text-center">{item.claimable_amount}</td>
                                            <td className="text-center">{item.unclaimable_amount}</td>
                                          </tr>
                                        );
                                      })}
                                      <tr>
                                        <th colSpan="5">Group Total</th>
                                        <th>{formatPrice(groupCardTotal.order_bill)}</th>
                                        <th>{formatPrice(groupCardTotal.vat)}</th>
                                        <th></th>
                                        <th>{formatPrice(groupCardTotal.discount_amount)}</th>
                                        <th>{formatPrice(groupCardTotal.total_payable)}</th>
                                        <th>{formatPrice(groupCardTotal.claimable_amount)}</th>
                                        <th>{formatPrice(groupCardTotal.unclaimable_amount)}</th>
                                      </tr>
                                    </React.Fragment>
                                  );
                                })
                              }
                              <tr>
                                <th colSpan="5">Grand Total</th>
                                <th>{formatPrice(grandCardTotal.order_bill)}</th>
                                <th>{formatPrice(grandCardTotal.vat)}</th>
                                <th></th>
                                <th>{formatPrice(grandCardTotal.discount_amount)}</th>
                                <th>{formatPrice(grandCardTotal.total_payable)}</th>
                                <th>{formatPrice(grandCardTotal.claimable_amount)}</th>
                                <th>{formatPrice(grandCardTotal.unclaimable_amount)}</th>
                              </tr>  
                            </tbody>
                          </>
                        )}

                        {/* Discount Report Section */}
                        {reportData?.discount_report && Object.keys(reportData.discount_report).length > 0 && (
                          <>
                            <thead>
                              <tr style={{height: "10px"}}></tr>
                              <tr><th colSpan="15" className="text-center font-weight-bold lg-text border-0 pt-4">Discount Report</th></tr>
                              <tr>
                                <th>S/L</th>
                                <th>User Name</th>
                                <th>Token</th>
                                <th>Branch</th>
                                <th style={{minWidth: "110px"}}>Order Date</th>
                                <th>Customer</th>
                                <th>Order Type</th>
                                <th>Party</th>
                                <th>Scheme</th>
                                <th>Sub Total</th>
                                <th>Discount</th>
                                <th>Tax</th>
                                <th>Service Amt</th>
                                <th>Delivery Ch</th>
                                <th>Total Payable</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.keys(reportData.discount_report).map((group, groupIndex) => {
                                  let groupDisTotal = {
                                    sub_total: 0,
                                    discount: 0,
                                    discount_amount: 0,
                                    vat: 0,
                                    service_amount: 0,
                                    service_charge: 0,
                                    total_payable: 0,
                                  };

                                  return (
                                    <React.Fragment key={`disc_group_${groupIndex}`}>
                                      <tr>
                                        <td colSpan="14" className="text-center font-weight-bold text-capitalize border-0 pt-2 pb-2 bg-light">
                                          {group}
                                        </td>
                                      </tr>
                                      {reportData.discount_report[group].map((item, i) => {
                                        groupDisTotal.sub_total += parseFloat(item.sub_total || 0);
                                        groupDisTotal.discount += parseFloat(item.discount || 0);
                                        groupDisTotal.discount_amount += parseFloat(item.discount_amount || 0);
                                        groupDisTotal.vat += parseFloat(item.vat || 0);
                                        groupDisTotal.service_amount += parseFloat(item.service_amount || 0);
                                        groupDisTotal.service_charge += parseFloat(item.service_charge || 0);
                                        groupDisTotal.total_payable += parseFloat(item.total_payable || 0);

                                        grandDisTotal.sub_total += parseFloat(item.sub_total || 0);
                                        grandDisTotal.discount += parseFloat(item.discount || 0);
                                        grandDisTotal.discount_amount += parseFloat(item.discount_amount || 0);
                                        grandDisTotal.vat += parseFloat(item.vat || 0);
                                        grandDisTotal.service_amount += parseFloat(item.service_amount || 0);
                                        grandDisTotal.service_charge += parseFloat(item.service_charge || 0);
                                        grandDisTotal.total_payable += parseFloat(item.total_payable || 0);

                                        return (
                                          <tr key={`disc_item_${i}`}>
                                            <td className="text-center">{i + 1}</td>
                                            <td className="text-center">{item.user_name}</td>
                                            <td className="text-center">{item.token_no}</td>
                                            <td className="text-center">{item.branch_name}</td>
                                            <td className="text-center">{item.order_date}</td>
                                            <td className="text-center">{item.customer_name || '-'}</td>
                                            <td className="text-center">{item.order_type}</td>
                                            <td className="text-center">{item.party_name}</td>
                                            <td className="text-center">{item.scheme_name}</td>
                                            <td className="text-center">{item.sub_total}</td>
                                            <td className="text-center">{item.discount_amount} ({item.discount}%)</td>
                                            <td className="text-center">{item.vat}</td>
                                            <td className="text-center">{item.service_amount}</td>
                                            <td className="text-center">{item.service_charge}</td>
                                            <td className="text-center">{item.total_payable}</td>
                                          </tr>
                                        );
                                      })}
                                      <tr>
                                        <th colSpan="9">Group Total</th>
                                        <th>{formatPrice(groupDisTotal.sub_total)}</th>
                                        <th>{formatPrice(groupDisTotal.discount_amount)}</th>
                                        <th>{formatPrice(groupDisTotal.vat)}</th>
                                        <th>{formatPrice(groupDisTotal.service_amount)}</th>
                                        <th>{formatPrice(groupDisTotal.service_charge)}</th>
                                        <th>{formatPrice(groupDisTotal.total_payable)}</th>
                                      </tr>
                                    </React.Fragment>
                                  );
                                })
                              }
                              <tr>
                                <th colSpan="9">Grand Total</th>
                                <th>{formatPrice(grandDisTotal.sub_total)}</th>
                                <th>{formatPrice(grandDisTotal.discount_amount)}</th>
                                <th>{formatPrice(grandDisTotal.vat)}</th>
                                <th>{formatPrice(grandDisTotal.service_amount)}</th>
                                <th>{formatPrice(grandDisTotal.service_charge)}</th>
                                <th>{formatPrice(grandDisTotal.total_payable)}</th>
                              </tr>
                            </tbody>
                          </>
                        )}
                        <tfoot style={{ border: "0px" }}>
                          <tr style={{ border: "0px" }}>
                            <td colSpan="15" className="text-center" style={{ border: "0px" }}>
                              <h5 className="mt-3">Powered by indolj.pk</h5>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
